import React, { useState, useEffect } from 'react';
import { Bell, Send, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import api from '@/api/api';
import { useReportPagination } from '@/components/ReportPagination';

const FeeReminders = () => {
  const [dueFees, setDueFees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [sending, setSending] = useState(false);
  const { paginatedData: pageFees, PaginationControls } = useReportPagination(dueFees);

  useEffect(() => {
    fetchDueFees();
  }, []);

  const fetchDueFees = async () => {
    try {
      const response = await api.get('/fees/due-fees');
      setDueFees(response.data);
    } catch (error) {
      console.error('Error fetching due fees:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectStudent = (studentId) => {
    setSelectedStudents(prev => {
      if (prev.includes(studentId)) {
        return prev.filter(id => id !== studentId);
      } else {
        return [...prev, studentId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedStudents.length === dueFees.length) {
      setSelectedStudents([]);
    } else {
      setSelectedStudents(dueFees.map(fee => fee.student_id));
    }
  };

  const handleSendReminders = async () => {
    if (selectedStudents.length === 0) {
      alert('Please select at least one student');
      return;
    }

    if (!window.confirm(`Send reminders to ${selectedStudents.length} student(s)?`)) {
      return;
    }

    setSending(true);
    try {
      const response = await api.post('/fees/send-reminders', {
        student_ids: selectedStudents
      });
      alert(response.data.message || 'Reminders sent successfully!');
      setSelectedStudents([]);
      fetchDueFees(); // Refresh the list
    } catch (error) {
      console.error('Error sending reminders:', error);
      alert(error.response?.data?.detail || 'Failed to send reminders');
    } finally {
      setSending(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  const calculateDaysOverdue = (dueDate) => {
    const due = new Date(dueDate);
    const today = new Date();
    const diffTime = today - due;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Bell className="text-blue-600" size={32} />
            Fee Reminders
          </h1>
          <p className="text-gray-600 mt-1">Send payment reminders to students with due fees</p>
        </div>
        <div className="flex gap-3">
          <Button
            onClick={handleSendReminders}
            disabled={selectedStudents.length === 0 || sending}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Send size={20} className="mr-2" />
            {sending ? 'Sending...' : `Send Reminders (${selectedStudents.length})`}
          </Button>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-red-100 rounded-lg">
              <Users className="text-red-600" size={24} />
            </div>
            <div>
              <p className="text-sm text-gray-600">Students with Due</p>
              <p className="text-2xl font-bold text-gray-900">{dueFees.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-orange-100 rounded-lg">
              <Bell className="text-orange-600" size={24} />
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Due Amount</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(dueFees.reduce((sum, fee) => sum + fee.due_amount, 0))}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-blue-100 rounded-lg">
              <Send className="text-blue-600" size={24} />
            </div>
            <div>
              <p className="text-sm text-gray-600">Selected for Reminder</p>
              <p className="text-2xl font-bold text-gray-900">{selectedStudents.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Due Fees List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-x-auto">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedStudents.length === dueFees.length && dueFees.length > 0}
                    onChange={handleSelectAll}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Student
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Course
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Due Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Due Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Days Overdue
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {dueFees.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                    No due fees found. All students are up to date!
                  </td>
                </tr>
              ) : (
                pageFees.map((fee) => {
                  const daysOverdue = calculateDaysOverdue(fee.due_date);
                  return (
                    <tr
                      key={fee.student_id}
                      className={`hover:bg-gray-50 ${
                        selectedStudents.includes(fee.student_id) ? 'bg-blue-50' : ''
                      }`}
                    >
                      <td className="px-6 py-4">
                        <input
                          type="checkbox"
                          checked={selectedStudents.includes(fee.student_id)}
                          onChange={() => handleSelectStudent(fee.student_id)}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {fee.student_name}
                        </div>
                        <div className="text-sm text-gray-500">{fee.admission_no}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{fee.course_name}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-semibold text-red-600">
                          {formatCurrency(fee.due_amount)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{fee.due_date}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          daysOverdue > 30 ? 'bg-red-100 text-red-800' :
                          daysOverdue > 7 ? 'bg-orange-100 text-orange-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {daysOverdue} days
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{fee.phone}</div>
                        <div className="text-sm text-gray-500">{fee.email}</div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <PaginationControls />
      </div>
    </div>
  );
};

export default FeeReminders;
